import {
  PRECO_MAXIMO_DO_PRODUTO,
  QUANTIDADE_MAXIMA_POR_MOVIMENTO,
  ROTULO_TIPO_MOVIMENTO,
  type CategoriaProduto,
  type InventarioNaLista,
  type MovimentoDoProduto,
  type ProdutoEdicao,
  type ProdutoNaTabela,
  type ProdutoNovo,
} from '@arena/shared/schemas';
import { dinheiro, paraCentavos } from '../agenda/formatos.js';
import { NOME_DO_PRODUTO, centavosParaCampo } from './formatos.js';

/**
 * O cadastro e o detalhe do produto (`#m-produto`) e o histórico de
 * inventários: as traduções puras, para a tela ficar só com o JSX.
 *
 * Duas regras moram aqui, e as duas são do servidor antes de serem da tela:
 *
 *   O CUSTO NÃO SE EDITA. Ele muda só por entrada de mercadoria (é o médio
 *   ponderado). O formulário de edição nem o preenche, e `edicaoDoFormulario`
 *   nunca o devolve — um cliente que o enviasse seria ignorado pelo zod, mas
 *   a tela não deve nem tentar.
 *
 *   O CUSTO QUE VEIO É O QUE SE MOSTRA. Colunas de custo (do movimento) e de
 *   perda (do inventário) existem quando o servidor as mandou; a ausência é da
 *   projeção, não zero.
 */

/** O produto no formulário: tudo texto, como a pessoa digita. */
export interface FormularioDeProduto {
  nome: string;
  categoria: CategoriaProduto;
  preco: string;
  minimo: string;
  /** Só na criação. Numa edição fica vazio e é ignorado. */
  custo: string;
  ativo: boolean;
}

export function formularioVazio(): FormularioDeProduto {
  return { nome: '', categoria: 'Bebida', preco: '', minimo: '', custo: '', ativo: true };
}

/**
 * O produto aberto, como o servidor o tem. O `custo` fica de fora de
 * propósito: nem o Dono edita o custo aqui, e o Gestão nem o recebeu.
 */
export function formularioDoProduto(
  produto: Pick<ProdutoNaTabela, 'nome' | 'categoria' | 'preco' | 'estoqueMinimo' | 'ativo'>,
): FormularioDeProduto {
  return {
    nome: produto.nome,
    categoria: produto.categoria,
    preco: centavosParaCampo(produto.preco),
    minimo: String(produto.estoqueMinimo),
    custo: '',
    ativo: produto.ativo,
  };
}

function valorDe(texto: string): number | null {
  const centavos = paraCentavos(texto);
  return centavos !== null && centavos <= PRECO_MAXIMO_DO_PRODUTO ? centavos : null;
}

function minimoDe(texto: string): number | null {
  const limpo = texto.trim();
  if (!/^\d+$/.test(limpo)) return null;
  const n = Number(limpo);
  return n <= QUANTIDADE_MAXIMA_POR_MOVIMENTO ? n : null;
}

function nomeValido(nome: string): boolean {
  const tamanho = nome.trim().length;
  return tamanho >= NOME_DO_PRODUTO.minimo && tamanho <= NOME_DO_PRODUTO.maximo;
}

export interface ProblemasDoProduto {
  nome?: string;
  preco?: string;
  minimo?: string;
  custo?: string;
}

const FAIXA_DE_VALOR = `Informe um valor entre ${dinheiro(0)} e ${dinheiro(PRECO_MAXIMO_DO_PRODUTO)}, como 3,20.`;

/**
 * O que impede de salvar. O custo só conta na criação: numa edição ele não
 * é lido, e cobrá-lo ou criticá-lo seria falar de um campo que nem existe.
 */
export function problemasDoProduto(
  form: FormularioDeProduto,
  modo: 'novo' | 'edicao',
): ProblemasDoProduto {
  const problemas: ProblemasDoProduto = {};
  if (!nomeValido(form.nome)) {
    problemas.nome = `Informe o nome, de ${NOME_DO_PRODUTO.minimo} a ${NOME_DO_PRODUTO.maximo} letras.`;
  }
  if (valorDe(form.preco) === null) problemas.preco = FAIXA_DE_VALOR;
  if (minimoDe(form.minimo) === null) {
    problemas.minimo = `Use um número inteiro de 0 a ${QUANTIDADE_MAXIMA_POR_MOVIMENTO}.`;
  }
  if (modo === 'novo' && valorDe(form.custo) === null) problemas.custo = FAIXA_DE_VALOR;
  return problemas;
}

/** O pedido de criação, ou `null` enquanto algo faltar. */
export function produtoNovoDoFormulario(form: FormularioDeProduto): ProdutoNovo | null {
  if (Object.keys(problemasDoProduto(form, 'novo')).length > 0) return null;
  const preco = valorDe(form.preco);
  const custo = valorDe(form.custo);
  const estoqueMinimo = minimoDe(form.minimo);
  if (preco === null || custo === null || estoqueMinimo === null) return null;
  return { nome: form.nome.trim(), categoria: form.categoria, custo, preco, estoqueMinimo };
}

/**
 * O pedido de edição: SÓ o que a pessoa mudou, e nunca o custo. O servidor
 * aplica tudo que chega, então mandar o que ela nem tocou é sobrescrever o
 * produto com o retrato que a tela tinha.
 */
export function edicaoDoFormulario(
  form: FormularioDeProduto,
  original: Pick<ProdutoNaTabela, 'nome' | 'categoria' | 'preco' | 'estoqueMinimo' | 'ativo'>,
): ProdutoEdicao {
  const nome = form.nome.trim();
  const preco = valorDe(form.preco);
  const minimo = minimoDe(form.minimo);
  return {
    ...(nome !== original.nome ? { nome } : {}),
    ...(form.categoria !== original.categoria ? { categoria: form.categoria } : {}),
    ...(preco !== null && preco !== original.preco ? { preco } : {}),
    ...(minimo !== null && minimo !== original.estoqueMinimo ? { estoqueMinimo: minimo } : {}),
    ...(form.ativo !== original.ativo ? { ativo: form.ativo } : {}),
  };
}

/** O produto aberto na URL (`?produto=<id>`), ou `null`. */
export function produtoDaUrl(params: URLSearchParams): string | null {
  const id = params.get('produto');
  return id ? id : null;
}

// ---------------------------------------------------------------------
// O histórico do produto
// ---------------------------------------------------------------------

export function rotuloDoMovimento(tipo: MovimentoDoProduto['tipo']): string {
  return ROTULO_TIPO_MOVIMENTO[tipo];
}

/** Os oito primeiros caracteres do id: o bastante para achar a origem, sem pessoa nenhuma. */
function curto(id: string): string {
  return `#${id.slice(0, 8)}`;
}

/** De onde veio o movimento: a entrada, a comanda ou o inventário. */
export function origemDoMovimento(
  movimento: Pick<MovimentoDoProduto, 'entradaId' | 'comandaId' | 'inventarioId'>,
): string {
  if (movimento.entradaId) return `Entrada ${curto(movimento.entradaId)}`;
  if (movimento.comandaId) return `Comanda ${curto(movimento.comandaId)}`;
  if (movimento.inventarioId) return `Inventário ${curto(movimento.inventarioId)}`;
  return '—';
}

/** "+20" na entrada, "−1" na saída: o sinal é a informação. */
export function quantidadeDoMovimento(quantidade: number): string {
  if (quantidade > 0) return `+${quantidade}`;
  if (quantidade < 0) return `−${Math.abs(quantidade)}`;
  return '0';
}

/** A coluna de custo só existe quando o servidor mandou o custo de algum movimento. */
export function movimentoComCusto(
  movimentos: readonly Pick<MovimentoDoProduto, 'custoUnitario'>[],
): boolean {
  return movimentos.some((movimento) => typeof movimento.custoUnitario === 'number');
}

// ---------------------------------------------------------------------
// O histórico de inventários
// ---------------------------------------------------------------------

/** A coluna Perda só existe quando o servidor mandou a perda (que é a custo). */
export function inventarioComPerda(
  inventarios: readonly Pick<InventarioNaLista, 'perdaValor'>[],
): boolean {
  return inventarios.some((inventario) => typeof inventario.perdaValor === 'number');
}

export function resumoDoInventario(inventario: Pick<InventarioNaLista, 'divergencias'>): string {
  const n = inventario.divergencias;
  if (n === 0) return 'Bateu';
  return `${n} ${n === 1 ? 'divergência' : 'divergências'}`;
}
